import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { warehouseAPI } from '../../api/warehouses';
import { AlertTriangle, Check } from 'lucide-react';
import toast from 'react-hot-toast';

interface ActiveAlert {
  warehouseId: number;
  warehouseName: string;
  itemId: number;
  itemName: string;
  currentStock: number;
  unit: string;
  minThreshold: number | null;
  maxThreshold: number | null;
  alertType: 'LOW_STOCK' | 'OVERSTOCK';
  severity: number;
  acknowledgedAt: Date | null;
  acknowledgedBy: string | null;
  lastAlertAt: Date | null;
}

export const StockAlertList: React.FC = () => {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState<ActiveAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    warehouseId: undefined as number | undefined,
    alertType: undefined as 'LOW_STOCK' | 'OVERSTOCK' | undefined,
    acknowledged: false,
  });
  const [warehouses, setWarehouses] = useState<any[]>([]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const result = await warehouseAPI.getActiveAlerts(filters);
      setAlerts(result);
    } catch (error) {
      console.error('Failed to load alerts:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      const result = await warehouseAPI.getAll();
      setWarehouses(result);
    } catch (error) {
      console.error('Failed to load warehouses:', error);
    }
  };

  useEffect(() => {
    loadWarehouses();
  }, []);

  useEffect(() => {
    loadAlerts();
  }, [filters]);

  const handleAcknowledge = async (warehouseId: number, itemId: number) => {
    try {
      await warehouseAPI.acknowledgeAlert(warehouseId, itemId);
      toast.success(t('warehouse.stockAlerts.acknowledged'));
      loadAlerts();
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
      toast.error(t('common.error'));
    }
  };

  const getAlertColor = (alertType: string) => {
    return alertType === 'LOW_STOCK' ? 'text-red-600 bg-red-50' : 'text-yellow-600 bg-yellow-50';
  };

  const getSeverityColor = (severity: number) => {
    if (severity >= 75) return 'text-red-700 font-bold';
    if (severity >= 50) return 'text-orange-600 font-semibold';
    return 'text-yellow-600';
  };

  if (loading) {
    return (
      <div className="rounded-lg border bg-white p-6">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 rounded bg-gray-200"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="rounded-lg border bg-white p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('warehouse.warehouse')}
            </label>
            <select
              value={filters.warehouseId || ''}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  warehouseId: e.target.value ? parseInt(e.target.value) : undefined,
                })
              }
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
            >
              <option value="">{t('common.all')}</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('warehouse.stockAlerts.alertType')}
            </label>
            <select
              value={filters.alertType || ''}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  alertType: e.target.value as 'LOW_STOCK' | 'OVERSTOCK' | undefined,
                })
              }
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
            >
              <option value="">{t('common.all')}</option>
              <option value="LOW_STOCK">{t('warehouse.stockAlerts.lowStock')}</option>
              <option value="OVERSTOCK">{t('warehouse.stockAlerts.overstock')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('common.status')}
            </label>
            <select
              value={filters.acknowledged ? 'true' : 'false'}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  acknowledged: e.target.value === 'true',
                })
              }
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
            >
              <option value="false">{t('warehouse.stockAlerts.active')}</option>
              <option value="true">{t('warehouse.stockAlerts.acknowledged')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Alerts Table */}
      <div className="overflow-hidden rounded-lg border bg-white shadow">
        {alerts.length === 0 ? (
          <div className="p-12 text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-4 text-gray-600">{t('warehouse.stockAlerts.noAlerts')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('warehouse.warehouse')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('item.item')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('warehouse.stockAlerts.currentStock')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('warehouse.stockAlerts.threshold')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('warehouse.stockAlerts.alertType')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('warehouse.stockAlerts.severity')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {alerts.map((alert) => (
                  <tr
                    key={`${alert.warehouseId}-${alert.itemId}`}
                    className={alert.alertType === 'LOW_STOCK' ? 'bg-red-50' : 'bg-yellow-50'}
                  >
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      {alert.warehouseName}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {alert.itemName}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      {alert.currentStock.toFixed(2)} {alert.unit}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      {alert.alertType === 'LOW_STOCK'
                        ? `Min: ${alert.minThreshold?.toFixed(2) || 'N/A'}`
                        : `Max: ${alert.maxThreshold?.toFixed(2) || 'N/A'}`}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getAlertColor(
                          alert.alertType
                        )}`}
                      >
                        {t(`warehouse.stockAlerts.${alert.alertType.toLowerCase()}`)}
                      </span>
                    </td>
                    <td className={`whitespace-nowrap px-6 py-4 text-sm ${getSeverityColor(alert.severity)}`}>
                      {alert.severity.toFixed(0)}%
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      {alert.acknowledgedAt ? (
                        <div className="text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <Check className="h-3 w-3 text-green-600" />
                            <span>{t('warehouse.stockAlerts.acknowledged')}</span>
                          </div>
                          <div>{alert.acknowledgedBy}</div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleAcknowledge(alert.warehouseId, alert.itemId)}
                          className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                        >
                          {t('warehouse.stockAlerts.acknowledge')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
